<template>
  <v-container ref="ProjectsGridTemplate" fluid class="pa-0">
    <v-row>
      <template v-if="rooms && rooms.asPM.length > 0">
        <v-col v-for="(project) in rooms.asPM"
               :key="project.id"
               cols="12"
               sm="6"
               md="3"
               lg="3"
               xl="2"
        >
          <span :draggable="!!projectsFoldersTree.length"
                @dragstart="dragRoomStart($event, project)"
                @dragend="dragRoomEnd($event, project)"
          >
            <ProjectsGrid
              :ref="`projectGrid${project.id}`"
              :project="project"
              :isPending="pmRoomsPending"
              :isRecentRoomsView="isRecentRoomsView"
            />
            <ProjectsGridGhost :ref="`projectGhost${project.id}`" :item="project"/>
          </span>
        </v-col>
        <v-col class="d-flex justify-end mt-4" cols="12">
          <AppPagination
            v-if="!hidePagination"
            :current-page="pmPagination.page"
            :page-size="pmPagination.pageSize"
            :total="pmPagination.itemCount"
            @update:current-page="emitPageChange('pm', $event)"
            @update:page-size="emitPageSizeChange('pm', $event)"
          >
            <template #label>
              {{ $t('projects.ProjectsGridTemplate.ItemsPerPage') }}
            </template>
          </AppPagination>
        </v-col>
      </template>
      <template v-if="rooms && rooms.asPM.length === 0 && rooms.asGuest.length > 0">
        <v-row>
          <v-col v-for="(project) in rooms.asGuest"
                 :key="project.id"
                 cols="12"
                 sm="6"
                 md="3"
                 lg="3"
                 xl="2"
          >
            <span :draggable="projectsFoldersTree.length"
                  @dragstart="dragRoomStart($event, project)"
                  @dragend="dragRoomEnd($event, project)"
            >
              <ProjectsGrid
                :ref="`projectGrid${project.id}`"
                :project="project"
                :isPending="guestRoomsPending"
                :isRecentRoomsView="isRecentRoomsView"
              />
              <ProjectsGridGhost :ref="`projectGhost${project.id}`" :item="project"/>
            </span>
          </v-col>
          <v-col class="d-flex justify-end mt-4" cols="12">
            <AppPagination
              v-if="!hidePagination"
              :current-page="guestPagination.page"
              :page-size="guestPagination.pageSize"
              :total="guestPagination.itemCount"
              @update:current-page="emitPageChange('guest', $event)"
              @update:page-size="emitPageSizeChange('guest', $event)"
            >
              <template #label>
                {{ $t('projects.ProjectsGridTemplate.ItemsPerPage') }}
              </template>
            </AppPagination>
          </v-col>
        </v-row>
      </template>
    </v-row>

    <template v-if="rooms && rooms.asPM.length > 0 && rooms.asGuest.length > 0">
      <v-row v-if="rooms.asPM.length > 0">
        <v-col>
          <div class="text-text-primary heading-sm-bold">
            {{ $t('projects.ProjectsGridTemplate.guest', { projectsCount: rooms.asGuest.length }) }}
          </div>
        </v-col>
      </v-row>
      <v-row>
        <v-col v-for="(project) in rooms.asGuest"
               :key="project.id"
               cols="12"
               sm="6"
               md="3"
               lg="3"
               xl="2"
        >
          <span :draggable="projectsFoldersTree.length"
                @dragstart="dragRoomStart($event, project)"
                @dragend="dragRoomEnd($event, project)"
          >
            <ProjectsGrid
              :ref="`projectGrid${project.id}`"
              :project="project"
              :isPending="guestRoomsPending"
              :isRecentRoomsView="isRecentRoomsView"
            />
            <ProjectsGridGhost :ref="`projectGhost${project.id}`" :item="project"/>
          </span>
        </v-col>
        <v-col class="d-flex justify-end mt-4" cols="12">
          <AppPagination
            v-if="!hidePagination"
            :current-page="guestPagination.page"
            :page-size="guestPagination.pageSize"
            :total="guestPagination.itemCount"
            @update:current-page="emitPageChange('guest', $event)"
            @update:page-size="emitPageSizeChange('guest', $event)"
          >
            <template #label>
              {{ $t('projects.ProjectsGridTemplate.ItemsPerPage') }}
            </template>
          </AppPagination>
        </v-col>
      </v-row>
    </template>
  </v-container>
</template>

<script>
import { mapState, mapMutations, mapGetters } from 'vuex'

import AppPagination from '@/common/pagination/AppPagination.vue'
import { DARK_BLUE_50, DARK_BLUE_500 } from '@/common/utils/colors'
import { SET_ROOM_TO_MOVE } from '@/store/modules/projects-folders/mutation_types'

import ProjectsGrid from './ProjectsGrid.vue'
import ProjectsGridGhost from './ProjectsGridGhost.vue'

export default {
  name: 'ProjectsGridTemplate',
  components: { ProjectsGrid, ProjectsGridGhost, AppPagination },
  props: {
    rooms: {
      required: true,
      validator: (item) => typeof item === 'object' || item === null,
    },
    hidePagination: {
      type: Boolean,
      default: false,
    },
    isRecentRoomsView: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      currentGhost: null,
      DARK_BLUE_50,
      DARK_BLUE_500,
    }
  },
  watch: {
    rooms: {
      handler() {
        this.pmPage = 1
        this.guestPage = 1
      },
      deep: true,
    },
  },
  computed: {
    ...mapState('projectsFolders', ['projectsFoldersTree']),
    ...mapGetters('user', ['currentUserId']),
    ...mapState('rooms', ['pmPagination', 'guestPagination', 'pmRoomsPending', 'guestRoomsPending']),
  },
  methods: {
    ...mapMutations('projectsFolders', [SET_ROOM_TO_MOVE]),
    dragRoomStart (event, room) {
      if (!this.projectsFoldersTree.length) return
      const projectGrid = this.$refs[`projectGrid${room.id}`][0].$el
      if (projectGrid) {
        projectGrid.classList.add('roomIsDragging')
      }
      const ghost = this.$refs[`projectGhost${room.id}`][0].$el.cloneNode(true)
      this.currentGhost = ghost
      if (this.currentGhost) {
        ghost.style.display = 'inline-flex'
        this.$refs.ProjectsGridTemplate.$el.appendChild(ghost)
        event.dataTransfer.setDragImage(ghost, 0, 0)
      }
      this.SET_ROOM_TO_MOVE(room)
    },
    dragRoomEnd (event, room) {
      if (!this.projectsFoldersTree.length) return
      const projectGrid = this.$refs[`projectGrid${room.id}`][0].$el
      if (projectGrid) {
        projectGrid.classList.remove('roomIsDragging')
      }
      if (this.currentGhost) {
        this.$refs.ProjectsGridTemplate.$el.removeChild(this.currentGhost)
      }
      this.SET_ROOM_TO_MOVE({})
    },
    emitPageChange(role, page) {
      this.$emit('update:page', { role, page })
    },
    emitPageSizeChange(role, pageSize) {
      this.$emit('update:page-size', { role, pageSize })
    },
  },
}
</script>

<style scoped lang="scss">
@import "@/scss/border-tokens";

:deep(.ProjectsGridTemplate-card) {
  width: 100%;
}

.roomIsDragging {
  background-color: rgb(var(--v-theme-surface-info));
  border: get-border('border-info-bold', 'border-width-2');

  :deep(.v-card__overlay) {
    opacity: 0;
  }
}
</style>
